import { useCallback, useEffect, useRef, useState } from 'react';

import type { ChatStatus } from 'ai';

import type { AskUIMessage } from './askUiMessage';

const NEAR_BOTTOM_PX = 48;

function isNearBottom(element: HTMLElement): boolean {
  return element.scrollHeight - element.scrollTop - element.clientHeight <= NEAR_BOTTOM_PX;
}

interface FollowLatestInputs {
  messages: ReadonlyArray<AskUIMessage>;
  status: ChatStatus;
  working: boolean;
  liveStatus: string | null;
}

export function useFollowLatest({ messages, status, working, liveStatus }: FollowLatestInputs) {
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const followLatestRef = useRef(true);
  const hasMessagesRef = useRef(messages.length > 0);
  hasMessagesRef.current = messages.length > 0;
  const [showJumpToLatest, setShowJumpToLatest] = useState(false);

  useEffect(() => {
    const element = scrollRef.current;
    if (!element || !messages.length) {
      return;
    }
    if (followLatestRef.current) {
      element.scrollTop = element.scrollHeight;
    } else if (working) {
      setShowJumpToLatest(true);
    }
  }, [messages, status, working, liveStatus]);

  useEffect(() => {
    const element = scrollRef.current;
    if (!element || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(() => {
      if (hasMessagesRef.current && followLatestRef.current) element.scrollTop = element.scrollHeight;
    });
    observer.observe(element);
    if (element.firstElementChild) observer.observe(element.firstElementChild);
    return () => observer.disconnect();
  }, []);

  const handleScroll = useCallback(() => {
    const element = scrollRef.current;
    if (!element) {
      return;
    }
    const near = isNearBottom(element);
    followLatestRef.current = near;
    if (near) {
      setShowJumpToLatest(false);
    }
  }, []);

  const jumpToLatest = useCallback(() => {
    const element = scrollRef.current;
    if (!element) {
      return;
    }
    followLatestRef.current = true;
    setShowJumpToLatest(false);
    element.scrollTo({ top: element.scrollHeight, behavior: 'smooth' });
  }, []);

  const followLatest = useCallback(() => {
    followLatestRef.current = true;
  }, []);

  const hideJumpToLatest = useCallback(() => setShowJumpToLatest(false), []);

  return { scrollRef, showJumpToLatest, handleScroll, jumpToLatest, followLatest, hideJumpToLatest };
}
